import { safeFormat } from "@/lib/safeDate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MapPin, User, Phone, ArrowLeftRight, Car, FileText, Calendar, Building2 } from "lucide-react";
import { format } from "date-fns";
import { resolveCollectionAddress, resolveCollectionPostcode } from "@/lib/jobAddressHelpers";
import { joinAddressAndPostcode } from "@/lib/addressFormat";

export interface JobSummaryData {
  vehicle_make: string;
  vehicle_model: string;
  vehicle_registration: string;
  vin_number?: string | null;
  delivery_address: string;
  delivery_postcode?: string | null;
  delivery_contact: string;
  collection_address?: string | null;
  collection_postcode?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  estimated_cost?: number | null;
  invoice_amount?: number | null;
  dispatch_notes?: string | null;
  is_return_leg?: boolean;
  has_return_vehicle?: boolean;
  return_vehicle_registration?: string | null;
  return_vehicle_make?: string | null;
  return_vehicle_model?: string | null;
  return_delivery_address?: string | null;
  return_delivery_postcode?: string | null;
  execution_type?: string;
  po_number?: string | null;
  customers?: {
    name: string;
    phone?: string;
    address?: string;
    postcode?: string | null;
  } | null;
  drivers?: {
    name: string;
  } | null;
}

interface JobSummaryDialogProps {
  job: JobSummaryData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function JobSummaryDialog({ job, open, onOpenChange }: JobSummaryDialogProps) {
  if (!job) return null;

  const collectionAddr = resolveCollectionAddress(job as any);
  const collectionPostcode = resolveCollectionPostcode(job as any);

  // Parse delivery contact name & phone
  const contactParts = job.delivery_contact?.split(/[-–—]?\s*(\+?\d[\d\s()]{8,})/) || [];
  const contactName = contactParts[0]?.trim()?.replace(/[-–—]\s*$/, '').trim() || job.delivery_contact;
  const contactPhone = contactParts[1]?.trim() || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Car className="w-4 h-4 text-primary" />
            Job Summary
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Vehicle */}
          <Section title="Vehicle">
            <p className="font-semibold text-sm">{job.vehicle_make} {job.vehicle_model}</p>
            <p className="text-xs text-muted-foreground font-mono">{job.vehicle_registration}</p>
            {job.vin_number && (
              <p className="text-xs text-muted-foreground">VIN: {job.vin_number}</p>
            )}
            {job.is_return_leg && (
              <Badge variant="outline" className="mt-1 text-[10px]">
                <ArrowLeftRight className="w-3 h-3 mr-1" />
                Return Leg
              </Badge>
            )}
          </Section>

          {/* Customer */}
          {job.customers && (
            <Section title="Customer">
              <p className="font-medium text-sm">{job.customers.name}</p>
              {job.customers.phone && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone className="w-3 h-3" />
                  {job.customers.phone}
                </p>
              )}
            </Section>
          )}

          {/* Delivery Contact */}
          {contactName && (
            <Section title="Delivery Contact">
              <p className="flex items-center gap-1.5 text-sm">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                {contactName}
              </p>
              {contactPhone && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground ml-5">
                  <Phone className="w-3 h-3" />
                  {contactPhone}
                </p>
              )}
            </Section>
          )}

          {/* Collection Address */}
          {collectionAddr && (
            <Section title="Collection Address">
              <p className="flex items-start gap-1.5 text-sm">
                <MapPin className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="break-words">{joinAddressAndPostcode(collectionAddr, collectionPostcode)}</span>
              </p>
            </Section>
          )}

          {/* Delivery Address */}
          <Section title="Delivery Address">
            <p className="flex items-start gap-1.5 text-sm">
              <MapPin className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="break-words">{joinAddressAndPostcode(job.delivery_address, job.delivery_postcode)}</span>
            </p>
          </Section>

          {/* Schedule */}
          {job.scheduled_date && (
            <Section title="Scheduled">
              <p className="flex items-center gap-1.5 text-sm">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                {safeFormat(job.scheduled_date, "EEE dd MMM yyyy")}
                {job.scheduled_time && ` at ${job.scheduled_time}`}
              </p>
            </Section>
          )}

          {/* Driver */}
          {job.drivers?.name && (
            <Section title="Assigned Driver">
              <p className="text-sm">{job.drivers.name}</p>
            </Section>
          )}

          {/* Subcontracted */}
          {job.execution_type === 'subcontracted' && (
            <Section title="Execution">
              <Badge variant="outline" className="text-[10px]">
                <Building2 className="w-3 h-3 mr-1" />
                Subcontracted
              </Badge>
            </Section>
          )}

          {/* Return Vehicle */}
          {job.has_return_vehicle && (
            <Section title="Return Vehicle">
              <Badge className="bg-accent/10 text-accent border-accent/20 text-xs">
                <ArrowLeftRight className="w-3 h-3 mr-1" />
                Return Vehicle Included
              </Badge>
              {job.return_vehicle_registration && (
                <p className="text-xs text-muted-foreground mt-1">
                  Reg: {job.return_vehicle_registration}
                </p>
              )}
              {job.return_delivery_address && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground mt-1">
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span className="break-words">
                    {joinAddressAndPostcode(job.return_delivery_address, job.return_delivery_postcode)}
                  </span>
                </p>
              )}
            </Section>
          )}


          {/* Driver Notes */}
          {job.dispatch_notes && (
            <Section title="Driver Notes">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{job.dispatch_notes}</p>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}
